import { Box, Button, Checkbox, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './Facility.module.scss';
import { IMasterDataItem } from '~/utils/interface/master';
import { ChangeEvent, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import locationApi from '~/apis/location';
import { cast, clearInputFilter, exportExcel, handleApiError } from '~/utils/helper';
import { AddNewIcon, ExportFillIcon } from '~/assets/icons';
import FacilityTable from './components/FacilityTable';
import utilityApi from '~/apis/utility';
import facilityApi from '~/apis/facility';
import { IFacility } from '~/utils/interface/facility';
import DetailFacilityDialog from './components/detail-facility-dialog/DetailFacilityDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditFacilityDialog from './components/edit-facility-dialog/EditFacilityDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import { notify } from '~/utils/helper';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';

const cx = classNames.bind(styles);

const initialValues: IFacility = {
  location_code: '',
  location_name: '',
  province: '',
  province_code: '',
  ward: '',
  ward_code: '',
  address: '',
  manager_name: '',
  manager_phone: '',
  manager_unit: '',
  periodic_check_status: [],
  status: [],
  check_date_from: '',
  check_date_to: '',
  order_by: {
    field: 'location_name',
    type: 'asc',
  },
  page_index: 1,
  page_size: 10,
};

const Facility = () => {
  const { t } = useTranslation(['admin/facility']);

  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [isOpenDetailFacilityDialog, setIsOpenDetailFacilityDialog] = useState<boolean>(false);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [manageUnitsOptions, setManageUnitsOptions] = useState<IMasterDataItem[]>();
  const [facility, setFacility] = useState<IFacility>();
  const [totalItems, setTotalItems] = useState(0);
  const [facilities, setFacilities] = useState<any>();
  const [refreshData, setRefreshData] = useState(false);
  const [facilityIds, setFacilityIds] = useState<Array<string>>([]);
  const [isOpenEditFacilityDialog, setIsOpenEditFacilityDialog] = useState<boolean>(false);
  const [editFacilityDialogMode, setEditFacilityDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [isOpenDeleteFacilityDialog, setIsOpenDeleteFacilityDialog] = useState<boolean>(false);

  useEffect(() => {
    if (refreshData) {
      handleGetListFacility();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  const form = useFormik<any>({
    initialValues,
    onSubmit: async () => {
      handleGetListFacility();
    },
  });

  const getListFacility = async (overrideParams = {}) => {
    try {
      const { province_code, ward_code, manager_unit, ...submitValues } = form.values;
      const status =
        form.values.status?.map((item: any) => {
          return Number(item);
        }) ?? [];

      const data: any = {
        ...submitValues,
        ...overrideParams,
        status,
      };

      if (province_code) {
        data.province = provinceOptions.find((p) => p.id === province_code)?.data0 || '';
      }

      if (ward_code) {
        data.ward = wardOptions.find((w) => w.id === ward_code)?.data0 || '';
      }

      if (!isNaN(Number(manager_unit))) {
        data.manager_unit =
          manageUnitsOptions?.find((manageUnit: any) => manageUnit.id === Number(manager_unit))?.name || '';
      } else {
        data.manager_unit = manager_unit;
      }

      const res = await facilityApi.getListFacility(data);
      return res.data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetListFacility = async () => {
    const response = await getListFacility();
    setTotalItems(response.total);
    setFacilities(response.data);
    setFacilityIds([]);
  };

  const statusOptions = [
    {
      id: '0',
      name: t('statusOptions.pending'),
    },
    {
      id: '1',
      name: t('statusOptions.deployed'),
    },
    {
      id: '2',
      name: t('statusOptions.activated'),
    },
  ];

  const periodicCheckStatusOptions = [
    {
      id: '0',
      name: t('periodicCheckStatusOptions.noDevice'),
    },
    {
      id: '1',
      name: t('periodicCheckStatusOptions.overDate'),
    },
    {
      id: '2',
      name: t('periodicCheckStatusOptions.valid'),
    },
    {
      id: '3',
      name: t('periodicCheckStatusOptions.dueDate'),
    },
  ];

  const getStatusStyle = (value: string) => {
    if (Number(value) === 0) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] py-[3px] px-[15px]'>
          {t('statusOptions.pending')}
        </Box>
      );
    } else if (Number(value) === 1) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] py-[3px] px-[15px]'>
          {t('statusOptions.deployed')}
        </Box>
      );
    } else if (Number(value) === 2) {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px] py-[3px] px-[15px]'>
          {t('statusOptions.activated')}
        </Box>
      );
    }
  };

  const getPeriodicCheckStatusStyle = (value: string) => {
    if (Number(value) === 0) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.noDevice')}
        </Box>
      );
    } else if (Number(value) === 1) {
      return (
        <Box className='text-[#F32A4C] border border-[#F32A4C] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.overDate')}
        </Box>
      );
    } else if (Number(value) === 3) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.dueDate')}
        </Box>
      );
    } else {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px] py-[3px] px-[15px]'>
          {t('periodicCheckStatusOptions.valid')}
        </Box>
      );
    }
  };

  const renderStatusOptionItem = (option: { id: string; name: string }, _index: number) => {
    const isChecked = !form.values?.status?.length
      ? false
      : form.values?.status?.map((item: any) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full flex'>
        <Checkbox checked={isChecked} className='mr-[7px]' />
        <Box className={`text-center flex-1 flex justify-center`}>{getStatusStyle(option.id)}</Box>
      </Box>
    );
  };

  const renderPeriodicCheckStatusOptionItem = (option: { id: string; name: string }, _index: number) => {
    const isChecked = !form.values?.periodic_check_status?.length
      ? false
      : form.values?.periodic_check_status?.map((item: any) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full flex'>
        <Checkbox checked={isChecked} className='mr-[7px]' />
        <Box className={`text-center flex-1 flex justify-center`}>{getPeriodicCheckStatusStyle(option.id)}</Box>
      </Box>
    );
  };

  const handleSetIsOpenEditFacilitytDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenEditFacilityDialog(isOpen);
    if (needRefresh) {
      handleGetListFacility();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await locationApi.getListProvinces();
        setProvinceOptions(
          response.data.map((item) => {
            return {
              id: item.Code,
              name: item.Code + ' - ' + item.Name,
              data0: item.Name,
              data1: item.NameWithType,
              data2: item.Type,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();

    (async () => {
      try {
        const response = await utilityApi.getOptionBusiness();
        const { management_units: managementUnits } = response.data;
        setManageUnitsOptions(managementUnits);
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);

  useEffect(() => {
    handleGetListFacility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size]);

  useEffect(() => {
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  useEffect(() => {
    if (form.values.province_code) {
      (async () => {
        try {
          const response = await locationApi.getListWards({ province: form.values.province_code ?? '' });
          setWardOptions(
            response.data.map((item) => {
              return {
                id: item.Code,
                name: item.Code + ' - ' + item.Name,
                data0: item.Name,
                data1: item.NameWithType,
                data2: item.Type,
                data3: item.ParentCode,
              };
            })
          );
        } catch (error) {
          setWardOptions([]);
          handleApiError(error);
        }
      })();
    } else setWardOptions([]);
  }, [form.values.province_code]);

  const handleCheckAll = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const res = await getListFacility({ page_index: 1, page_size: totalItems });
      setFacilityIds(res.data?.map((facility: IFacility) => cast(facility.location_id)));
    } else {
      setFacilityIds([]);
    }
  };

  const handleCheck = async (row: IFacility, checked: boolean) => {
    if (checked) {
      setFacilityIds((prev) => [...(prev ?? []), ...[cast(row.location_id)]]);
    } else {
      setFacilityIds(facilityIds.filter((facilityId) => facilityId !== row.location_id));
    }
  };

  const getStatus = (value: any) => {
    if (Number(value) === 0) {
      return t('statusOptions.pending');
    } else if (Number(value) === 1) {
      return t('statusOptions.deployed');
    } else if (Number(value) === 2) {
      return t('statusOptions.activated');
    }
  };
  const getPeriodicCheckStatus = (value: any) => {
    if (Number(value) === 0) {
      t('periodicCheckStatusOptions.noDevice');
    } else if (Number(value) === 1) {
      return t('periodicCheckStatusOptions.overDate');
    } else if (Number(value) === 3) {
      return t('periodicCheckStatusOptions.dueDate');
    } else {
      return t('periodicCheckStatusOptions.valid');
    }
  };

  const formatLocationCode = (rawCode: string, province_code: string, ward_code: string) => {
    if (!rawCode || !province_code) return rawCode;

    if (rawCode.includes('_')) return rawCode;

    let result = '';
    let remaining = rawCode;

    if (remaining.startsWith(province_code)) {
      result += province_code;
      remaining = remaining.slice(province_code.length);
    } else {
      result += province_code;
    }

    if (ward_code && remaining.startsWith(ward_code)) {
      remaining = remaining.slice(ward_code.length);
      result += `_${ward_code}_${remaining}`;
    } else {
      result += `_${remaining}`;
    }

    return result;
  };

  const exportFile = async () => {
    if (!facilityIds.length) return notify('error', t('emptyExportFile'));
    const response = await getListFacility({ page_index: 1, page_size: totalItems });

    let res = response.data;
    if (facilityIds.length) {
      const filterSet = new Set(facilityIds);
      res = response.data?.filter((item) => filterSet.has(cast(item.location_id)));
      if (!res.length) {
        setFacilityIds([]);
        res = response.data;
      }
    }

    const columns = [
      { header: t('locationCode'), key: 'location_code', width: 15 },
      { header: t('locationName'), key: 'location_name', width: 15 },
      { header: t('province'), key: 'province', width: 15 },
      { header: t('ward'), key: 'ward', width: 15 },
      { header: t('address'), key: 'address', width: 20 },
      { header: t('managerPhone'), key: 'manager_phone', width: 15 },
      { header: t('managerUnit'), key: 'manager_unit', width: 15 },
      { header: t('status'), key: 'status', width: 15 },
      { header: t('periodicCheck'), key: 'periodic_check_status', width: 10 },
      { header: t('createdAt'), key: 'created_at', width: 15 },
      { header: t('updatedAt'), key: 'updated_at', width: 15 },
    ];

    const data = res?.map((item, index) => ({
      ...item,
      no: index + 1,
      status: getStatus(item.status),
      periodic_check_status: getPeriodicCheckStatus(item.periodic_check_status),
      location_code: formatLocationCode(item.location_code ?? '', item.province_code ?? '', item.ward_code ?? ''),
    }));

    exportExcel(columns, data, t('fileName'));
  };

  const handleDeleteFacility = async (locationId: string | number) => {
    try {
      await facilityApi.delete({
        location_id: Number(locationId),
      });
      notify('success', t('deleteFacilityDialog.deleteSuccess'));
      setRefreshData(true);
      setIsOpenDeleteFacilityDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <Box className={cx('w-full', 'facility-screen')}>
      <Box className='w-full flex justify-between'>
        <Box className={cx('box-container')}>
          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='province_code'>{t('province')}</FormLabel>
              <BaseSelect
                options={provinceOptions}
                value={form.values.province_code}
                placeholder={t('select')}
                name='province_code'
                isSearchEnable
                onChange={form.handleChange}
                displayInvalidValue
                isDisplayAllOption
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='ward_code'>{t('ward')}</FormLabel>
              <BaseSelect
                options={wardOptions}
                value={form.values.ward_code}
                placeholder={t('select')}
                name='ward_code'
                isSearchEnable
                onChange={form.handleChange}
                displayInvalidValue
                isDisplayAllOption
              />
            </Box>
          </Box>

          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='manager_unit'>{t('managerUnit')}</FormLabel>
              <BaseSelect
                options={manageUnitsOptions}
                value={form.values.manager_unit}
                placeholder={t('select')}
                name='manager_unit'
                isSearchEnable
                onChange={form.handleChange}
                displayInvalidValue
                isDisplayAllOption
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='status'>{t('statusFacility')}</FormLabel>
              <BaseSelect
                options={statusOptions}
                value={form.values.status ?? []}
                placeholder={t('select')}
                name='status'
                isSearchEnable
                onChange={(e) => {
                  form.setFieldValue('status', e.target.value);
                }}
                multiple
                selectAllOptions={(data) => {
                  form.setFieldValue('status', data);
                }}
                renderOptionItem={renderStatusOptionItem}
              />
            </Box>
          </Box>

          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='periodic_check_status'>{t('periodicCheckStatus')}</FormLabel>
              <BaseSelect
                options={periodicCheckStatusOptions}
                value={form.values.periodic_check_status ?? []}
                placeholder={t('select')}
                name='periodic_check_status'
                isSearchEnable
                onChange={(e) => {
                  form.setFieldValue('periodic_check_status', e.target.value);
                }}
                multiple
                selectAllOptions={(data) => {
                  form.setFieldValue('periodic_check_status', data);
                }}
                renderOptionItem={renderPeriodicCheckStatusOptionItem}
              />
            </Box>
          </Box>

          <Box className='flex items-center gap-[10px]'>
            <FormLabel htmlFor='registrationDate'>{t('registrationDate')}</FormLabel>
            <Box className={`${cx('column-text-field')} justify-between`}>
              <Box className={cx('row-text-field')}>
                <FormLabel htmlFor='date_from'>{t('dateFrom')}</FormLabel>
                <BaseDatePicker
                  portalId='datePicker-portalId'
                  value={form.values.check_date_from}
                  dateFormat='YYYY-MM-DD'
                  onChange={(date) => {
                    form.setFieldValue('check_date_from', date);
                  }}
                  placeholder={t('select')}
                  calendarProps={form.values.check_date_to ? { maxDate: new Date(form.values.check_date_to) } : {}}
                />
              </Box>
              <Box className={cx('row-text-field')}>
                <FormLabel htmlFor='date_to'>{t('dateTo')}</FormLabel>
                <BaseDatePicker
                  portalId='datePicker-portalId'
                  value={form.values.check_date_to}
                  dateFormat='YYYY-MM-DD'
                  onChange={(date) => {
                    form.setFieldValue('check_date_to', date);
                  }}
                  placeholder={t('select')}
                  calendarProps={form.values.check_date_from ? { minDate: new Date(form.values.check_date_from) } : {}}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Button
          className='bg-[#ED1002] rounded-[20px] text-white w-[130px] h-[35px] hover:bg-[#ED1002] hover:opacity-80 hover:text-white'
          onClick={() => {
            clearInputFilter();
            form.resetForm();
          }}
        >
          {t('clearFilter')}
        </Button>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          my: '14px',
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-[2px] px-5 justify-between items-center'>
          <Typography className='text-base font-bold text-[white]'>{t('jobList')}</Typography>
          <Box className='flex pr-10'>
            <Button
              startIcon={<AddNewIcon />}
              className='text-white font-semibold'
              onClick={() => {
                setEditFacilityDialogMode(DIALOG_MODE_CREATE);
                handleSetIsOpenEditFacilitytDialog(true);
              }}
            >
              {t('addNew')}
            </Button>
            <Button startIcon={<ExportFillIcon />} className='text-white  font-semibold' onClick={exportFile}>
              {t('fileExport')}
            </Button>
          </Box>
        </Box>
        <FacilityTable
          pageSize={form.values.page_size ?? 10}
          data={facilities}
          form={form}
          onDetail={(row) => {
            setFacility(row);
            setIsOpenDetailFacilityDialog(true);
          }}
          facilityIds={facilityIds}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
          onEdit={(row) => {
            setFacility(row);
            setEditFacilityDialogMode(DIALOG_MODE_EDIT);
            handleSetIsOpenEditFacilitytDialog(true);
          }}
          onDelete={(row) => {
            setFacility(row);
            setIsOpenDeleteFacilityDialog(true);
          }}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('facility')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={[
              { id: 10, name: '10' },
              { id: 20, name: '20' },
              { id: 50, name: '50' },
              { id: 100, name: '100' },
              { id: 250, name: '250' },
              { id: 500, name: '500' },
            ]}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>
      <EditFacilityDialog
        open={isOpenEditFacilityDialog}
        mode={editFacilityDialogMode}
        setOpen={handleSetIsOpenEditFacilitytDialog}
        locationId={facility?.location_id}
      />
      <DetailFacilityDialog open={isOpenDetailFacilityDialog} data={facility} setOpen={setIsOpenDetailFacilityDialog} />

      <ConfirmDialog
        open={isOpenDeleteFacilityDialog}
        title={t('deleteFacilityDialog.title')}
        description={t('deleteFacilityDialog.description', {
          facilityId: facility?.location_code,
        })}
        onConfirm={() => handleDeleteFacility(facility?.location_id ?? '')}
        onCancel={() => setIsOpenDeleteFacilityDialog(false)}
      />
    </Box>
  );
};

export default Facility;
